/**
 * 線路描画ロジック
 */

import React from 'react';
import { debug } from '../../utils/debug';
import { getEdgeMidpoint, getTileConnections, pairMatches } from './utils';

/** デフォルト線路色（後方互換性のため） */
const DEFAULT_TRACK_COLOR = 'hsl(45, 60%, 35%)';

/**
 * タイルの線路パターンをSVG要素として描画
 * @param highlightedPairs - 強調するEdgeペアのリスト（指定されている場合、そのペアのみ強調）
 * @param ownerColors - 各線路の所有者色配列（connectionIndex順、未指定時はデフォルト茶色）
 */
export function renderTileTracks(
  tileType: string,
  cx: number,
  cy: number,
  hexSize: number,
  isTemporary = false,
  hasError = false,
  isHighlighted = false,
  highlightedPairs?: Array<{ pair: [number, number]; segmentIndex: number }>,
  ownerColors?: string[],
  totalHighlightCount?: number
): React.ReactElement[] {
  const connections = getTileConnections(tileType);
  const isTownTile = tileType.startsWith('TOWN_');

  // 各線路の色を取得（配列がない場合はデフォルト色）
  const getColorForConnection = (index: number): string => {
    if (!ownerColors || ownerColors.length === 0) {
      return DEFAULT_TRACK_COLOR;
    }
    // 配列が1要素の場合は全線路に適用、それ以外はindex順
    return ownerColors[index] ?? ownerColors[0] ?? DEFAULT_TRACK_COLOR;
  };

  // DEBUG
  if (highlightedPairs && highlightedPairs.length > 0) {
    debug.log(`[renderTileTracks] tileType=${tileType}, connections=`, connections, 'highlightedPairs=', highlightedPairs);
  }

  const elements: React.ReactElement[] = [];

  connections.forEach((conn, idx) => {
    // このペアを強調するかどうかを判定
    let shouldHighlight = isHighlighted;
    let isReverseDirection = false;  // 経路の進行方向が線の描画方向と逆かどうか
    let matchedSegmentIndex: number | undefined;  // マッチしたペアのsegmentIndex
    if (highlightedPairs && highlightedPairs.length > 0) {
      // 特定のペアが指定されている場合、そのペアのみ強調
      const matchResult = highlightedPairs.map(hp => ({ ...hp, matches: pairMatches(hp.pair, conn) }));
      const matchedPair = matchResult.find(r => r.matches);
      shouldHighlight = !!matchedPair;
      matchedSegmentIndex = matchedPair?.segmentIndex;
      debug.log(`[renderTileTracks] conn=${JSON.stringify(conn)}, matchResult=`, matchResult, 'shouldHighlight=', shouldHighlight, 'segmentIndex=', matchedSegmentIndex);

      // 経路の進行方向を判定: matchedPair.pair[0]が入口、pair[1]が出口
      // シンプルなルール：
      // - pair[0]（入口）が-1でない場合、それが経路の入口エッジ
      // - pair[0]（入口）が-1の場合、中心から辺への方向
      if (matchedPair) {
        const pair = matchedPair.pair;
        if (conn.length === 1) {
          // 町タイル: 辺から中心への線として描画される（edge → center）
          // pair[0] === -1 なら中心から辺への方向（描画方向と逆）→ reverse
          isReverseDirection = pair[0] === -1;
        } else if (conn.length === 2) {
          // 通常タイル: conn[0]からconn[1]への線として描画される
          if (pair[0] !== -1 && pair[1] !== -1) {
            // 両端がエッジの場合: pair[0]が入口、pair[1]が出口
            // conn[0]が出口（pair[1]）と一致すれば逆方向
            isReverseDirection = conn[0] === pair[1];
          } else {
            // 片方が-1（町タイル上の通常線路、または部分強調）
            // pair[0]が-1でない場合: pair[0]が入口エッジ
            // pair[0]が-1の場合: pair[1]が出口エッジ、中心から出口への方向
            const entryEdge = pair[0] !== -1 ? pair[0] : null;
            const exitEdge = pair[1] !== -1 ? pair[1] : null;
            if (entryEdge !== null) {
              // 入口エッジがconn[1]と一致すれば逆方向
              isReverseDirection = conn[1] === entryEdge;
            } else if (exitEdge !== null) {
              // 出口エッジがconn[0]と一致すれば逆方向
              isReverseDirection = conn[0] === exitEdge;
            }
          }
        }
        debug.log(`[renderTileTracks] direction: pair=${JSON.stringify(pair)}, conn=${JSON.stringify(conn)}, isReverseDirection=${isReverseDirection}`);
      }
    }

    // ハイライト時の色: 明るい緑（前景）と暗い緑（背景）
    const highlightColorBright = 'hsl(142, 76%, 42%)';  // 明るい緑
    const highlightColorDark = 'hsl(142, 76%, 36%)';    // 暗い緑
    const trackColor = shouldHighlight
      ? highlightColorBright
      : isTemporary
        ? (hasError ? 'hsl(var(--destructive))' : 'hsl(var(--primary))')
        : getColorForConnection(idx);
    const trackWidth = shouldHighlight ? 6 : (isTemporary ? 5 : 4);
    // ウェーブエフェクト: 経路上の順序に基づいてanimation-delayを設定
    // stroke-dashoffsetアニメーションで線路に沿って光が流れる
    const waveDelay = (matchedSegmentIndex !== undefined && totalHighlightCount && totalHighlightCount > 0)
      ? `${(matchedSegmentIndex / totalHighlightCount) * 1.0}s`  // 1.0秒かけて波が伝播
      : '0s';
    const highlightStyle = shouldHighlight ? {
      strokeDasharray: '15 15',  // 明暗が同じ長さで途切れなく
      animation: 'track-wave 1.0s linear infinite',
      animationDelay: waveDelay,
      animationDirection: isReverseDirection ? 'reverse' : 'normal'
    } as React.CSSProperties : undefined;

    if (conn.length === 1) {
      // 町タイル: 辺から中心への線
      const edge = getEdgeMidpoint(cx, cy, hexSize, conn[0]);
      // ハイライト時は2本の線を重ねる（暗い緑の背景 + 明るい緑のダッシュ）
      if (shouldHighlight) {
        elements.push(
          <line
            key={`track-${idx}-bg`}
            x1={edge.x}
            y1={edge.y}
            x2={cx}
            y2={cy}
            stroke={highlightColorDark}
            strokeWidth={trackWidth}
            strokeLinecap="round"
          />
        );
      }
      elements.push(
        <line
          key={`track-${idx}`}
          x1={edge.x}
          y1={edge.y}
          x2={cx}
          y2={cy}
          stroke={trackColor}
          strokeWidth={trackWidth}
          strokeLinecap="round"
          style={highlightStyle}
        />
      );
    } else if (conn.length === 2) {
      // 通常タイル: 辺から辺への線
      const edge1 = getEdgeMidpoint(cx, cy, hexSize, conn[0]);
      const edge2 = getEdgeMidpoint(cx, cy, hexSize, conn[1]);

      // 直線か曲線かを判定（対角なら直線、それ以外は曲線）
      const diff = Math.abs(conn[0] - conn[1]);
      const isStraight = diff === 3;

      // 部分強調チェック（-1 = 中央からの半分のみ強調）
      // まず、どちらのエッジが -1 ペアでターゲットされているかを確認
      const targetedEdges = new Set<number>();
      highlightedPairs?.forEach(hp => {
        if (hp.pair[0] === -1 && conn.includes(hp.pair[1])) targetedEdges.add(hp.pair[1]);
        if (hp.pair[1] === -1 && conn.includes(hp.pair[0])) targetedEdges.add(hp.pair[0]);
      });

      // 両端がターゲットされている場合（線路全体が使用されている）は全体強調
      const isBothEndsUsed = targetedEdges.has(conn[0]) && targetedEdges.has(conn[1]);

      const partialHighlightEdge = !isBothEndsUsed ? highlightedPairs?.find(hp =>
        (hp.pair[0] === -1 || hp.pair[1] === -1) &&
        (hp.pair[0] === conn[0] || hp.pair[0] === conn[1] || hp.pair[1] === conn[0] || hp.pair[1] === conn[1])
      ) : undefined;
      const shouldPartialHighlight = shouldHighlight && partialHighlightEdge && !isBothEndsUsed;

      // DEBUG
      if (highlightedPairs && highlightedPairs.length > 0) {
        debug.log(`[renderTileTracks] targetedEdges=`, [...targetedEdges], 'isBothEndsUsed=', isBothEndsUsed, 'partialHighlightEdge=', partialHighlightEdge, 'shouldPartialHighlight=', shouldPartialHighlight);
      }

      if (shouldPartialHighlight) {
        // 部分強調：2本の線に分けて描画（中央から各辺へ）
        const targetEdge = partialHighlightEdge.pair[0] === -1 ? partialHighlightEdge.pair[1] : partialHighlightEdge.pair[0];
        const isTargetEdge1 = conn[0] === targetEdge;

        // DEBUG
        debug.log(`[renderTileTracks] PARTIAL HIGHLIGHT EXECUTING: conn=${JSON.stringify(conn)}, targetEdge=${targetEdge}, isTargetEdge1=${isTargetEdge1}, trackColor=${trackColor}, isStraight=${isStraight}`);

        // 非強調部分（通常色）
        const normalColor = isTemporary
          ? (hasError ? 'hsl(var(--destructive))' : 'hsl(var(--primary))')
          : getColorForConnection(idx);
        const normalWidth = isTemporary ? 5 : 4;

        // 中点を計算（曲線の場合はベジェの中間点）
        if (isStraight) {
          // 直線：2本に分割
          // ハイライト部分のみ背景線を追加
          if (isTargetEdge1) {
            elements.push(
              <line
                key={`track-${idx}-part1-bg`}
                x1={edge1.x}
                y1={edge1.y}
                x2={cx}
                y2={cy}
                stroke={highlightColorDark}
                strokeWidth={trackWidth}
                strokeLinecap="round"
              />
            );
          }
          elements.push(
            <line
              key={`track-${idx}-part1`}
              x1={edge1.x}
              y1={edge1.y}
              x2={cx}
              y2={cy}
              stroke={isTargetEdge1 ? trackColor : normalColor}
              strokeWidth={isTargetEdge1 ? trackWidth : normalWidth}
              strokeLinecap="round"
              style={isTargetEdge1 ? highlightStyle : undefined}
            />
          );
          if (!isTargetEdge1) {
            elements.push(
              <line
                key={`track-${idx}-part2-bg`}
                x1={cx}
                y1={cy}
                x2={edge2.x}
                y2={edge2.y}
                stroke={highlightColorDark}
                strokeWidth={trackWidth}
                strokeLinecap="round"
              />
            );
          }
          elements.push(
            <line
              key={`track-${idx}-part2`}
              x1={cx}
              y1={cy}
              x2={edge2.x}
              y2={edge2.y}
              stroke={!isTargetEdge1 ? trackColor : normalColor}
              strokeWidth={!isTargetEdge1 ? trackWidth : normalWidth}
              strokeLinecap="round"
              style={!isTargetEdge1 ? highlightStyle : undefined}
            />
          );
        } else {
          // 曲線：2本のベジェ曲線に分割
          // 元の曲線 M edge1 Q center edge2 を2本に分割
          // 制御点を調整して自然な曲線にする
          const ctrl1X = (edge1.x + cx) / 2;
          const ctrl1Y = (edge1.y + cy) / 2;
          const ctrl2X = (cx + edge2.x) / 2;
          const ctrl2Y = (cy + edge2.y) / 2;

          // ハイライト部分のみ背景線を追加
          if (isTargetEdge1) {
            elements.push(
              <path
                key={`track-${idx}-part1-bg`}
                d={`M ${edge1.x} ${edge1.y} Q ${ctrl1X} ${ctrl1Y} ${cx} ${cy}`}
                fill="none"
                stroke={highlightColorDark}
                strokeWidth={trackWidth}
                strokeLinecap="round"
              />
            );
          }
          elements.push(
            <path
              key={`track-${idx}-part1`}
              d={`M ${edge1.x} ${edge1.y} Q ${ctrl1X} ${ctrl1Y} ${cx} ${cy}`}
              fill="none"
              stroke={isTargetEdge1 ? trackColor : normalColor}
              strokeWidth={isTargetEdge1 ? trackWidth : normalWidth}
              strokeLinecap="round"
              style={isTargetEdge1 ? highlightStyle : undefined}
            />
          );
          if (!isTargetEdge1) {
            elements.push(
              <path
                key={`track-${idx}-part2-bg`}
                d={`M ${cx} ${cy} Q ${ctrl2X} ${ctrl2Y} ${edge2.x} ${edge2.y}`}
                fill="none"
                stroke={highlightColorDark}
                strokeWidth={trackWidth}
                strokeLinecap="round"
              />
            );
          }
          elements.push(
            <path
              key={`track-${idx}-part2`}
              d={`M ${cx} ${cy} Q ${ctrl2X} ${ctrl2Y} ${edge2.x} ${edge2.y}`}
              fill="none"
              stroke={!isTargetEdge1 ? trackColor : normalColor}
              strokeWidth={!isTargetEdge1 ? trackWidth : normalWidth}
              strokeLinecap="round"
              style={!isTargetEdge1 ? highlightStyle : undefined}
            />
          );
        }
      } else {
        // 通常描画（全体を1本で描画）
        // ハイライト時は2本の線を重ねる（暗い緑の背景 + 明るい緑のダッシュ）
        if (isStraight) {
          if (shouldHighlight) {
            elements.push(
              <line
                key={`track-${idx}-bg`}
                x1={edge1.x}
                y1={edge1.y}
                x2={edge2.x}
                y2={edge2.y}
                stroke={highlightColorDark}
                strokeWidth={trackWidth}
                strokeLinecap="round"
              />
            );
          }
          elements.push(
            <line
              key={`track-${idx}`}
              x1={edge1.x}
              y1={edge1.y}
              x2={edge2.x}
              y2={edge2.y}
              stroke={trackColor}
              strokeWidth={trackWidth}
              strokeLinecap="round"
              style={highlightStyle}
            />
          );
        } else {
          // 曲線: ベジェ曲線で描画
          if (shouldHighlight) {
            elements.push(
              <path
                key={`track-${idx}-bg`}
                d={`M ${edge1.x} ${edge1.y} Q ${cx} ${cy} ${edge2.x} ${edge2.y}`}
                fill="none"
                stroke={highlightColorDark}
                strokeWidth={trackWidth}
                strokeLinecap="round"
              />
            );
          }
          elements.push(
            <path
              key={`track-${idx}`}
              d={`M ${edge1.x} ${edge1.y} Q ${cx} ${cy} ${edge2.x} ${edge2.y}`}
              fill="none"
              stroke={trackColor}
              strokeWidth={trackWidth}
              strokeLinecap="round"
              style={highlightStyle}
            />
          );
        }
      }
    }
  });

  // 町タイルの中心円
  if (isTownTile) {
    elements.push(
      <circle
        key="town-circle"
        cx={cx}
        cy={cy}
        r={hexSize * 0.35}
        fill="white"
        stroke="hsl(var(--foreground))"
        strokeWidth={1.5}
      />
    );
  }

  return elements;
}
